import './App.css';
import {ResponsiveRadar} from '@nivo/radar'
import multnomahData from './data/multnomah.json';
import washingtonData from './data/washington.json';
import clarkData from './data/clark.json';
import honoluluData from './data/honolulu.json';
import larimerData from './data/larimer.json';
import denverData from './data/denver.json';
import elpasoData from './data/elpaso.json';
import TestPositivitySankey from "./component/TestPositivitySankey";
import React, {useState} from "react";
import TestPositivity from "./component/TestPositivity";
import CaseDensity from "./component/CaseDensity";
import Rt from "./component/Rt";
import Vaccination from "./component/Vaccination";
import Icu from "./component/Icu";

// TODO do better at reporting most recent date. And title graphs.

export interface DataShape {
    actuals: any;
    actualsTimeseries: any[];
    annotations: any;
    cdcTransmissionLevel: number;
    cdcTransmissionLevelTimeseries: {
        date: string,
        cdcTransmissionLevel: number
    }[];
    country: string;
    county: string;
    fips: string;
    lastUpdatedDate: string;
    lat: any;
    level: string;
    locationId: string;
    long: any;
    metrics: any;
    metricsTimeseries: any[];
    population: number;
    riskLevels: any;
    riskLevelsTimeseries: any[];
    state: string;
    url: string;
}

export const getValidValue = (value: number | any | undefined, defaultVal: number): number => {
    return (value !== null && value !== undefined) ? value : defaultVal;
}

export const getRecent = (timeSeries: any[], compare: (input: number | any | undefined, output: number) => number): number => {
    return timeSeries.reduce<number>((acc, val) => {
        return compare(val, acc)
    }, 0);
}

interface SignalData {
    color: string,
    rt: number,
    cases: number,
    tests: number,
    icu: number
}

const signal = (data: DataShape): SignalData => {
    const rtRecent = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.infectionRate, acc));
    const casesRecent = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.caseDensity, acc));
    const testsRecent = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.testPositivityRatio, acc));
    const icuRecent = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.icuCapacityRatio, acc));

    return {
        color: (rtRecent > 1) || (casesRecent > 10) || (testsRecent > 0.05) || (icuRecent >= 0.85) ? 'red' : 'green',
        rt: rtRecent,
        cases: casesRecent,
        tests: testsRecent,
        icu: icuRecent
    }
}

const signals: { [key: string]: SignalData } = {
    'multnomah': signal(multnomahData),
    'washington': signal(washingtonData),
    'honolulu': signal(honoluluData),
    'clark': signal(clarkData),
    'larimer': signal(larimerData),
    'denver': signal(denverData),
    'elpaso': signal(elpasoData),
}

const badgeData = [
    {
        'signal': 'rt',
        'multnomah': signals.multnomah.rt,
        'washington': signals.washington.rt,
        'honolulu': signals.honolulu.rt,
        'clark': signals.clark.rt,
        'larimer': signals.larimer.rt,
        'denver': signals.denver.rt,
        'elpaso': signals.elpaso.rt,
    },
    {
        'signal': 'caseDensity',
        'multnomah': signals.multnomah.cases / 10,
        'washington': signals.washington.cases / 10,
        'honolulu': signals.honolulu.cases / 10,
        'clark': signals.clark.cases / 10,
        'larimer': signals.larimer.cases / 10,
        'denver': signals.denver.cases / 10,
        'elpaso': signals.elpaso.cases / 10,
    },
    {
        'signal': 'testPositivity',
        'multnomah': signals.multnomah.tests / 0.05,
        'washington': signals.washington.tests / 0.05,
        'honolulu': signals.honolulu.tests / 0.05,
        'clark': signals.clark.tests / 0.05,
        'larimer': signals.larimer.tests / 0.05,
        'denver': signals.denver.tests / 0.05,
        'elpaso': signals.elpaso.tests / 0.05,
    },
    {
        /**
         * Normally we expect ICU to be between 60% and 85%, where 85% is bad. We can basically
         * disregard values below 60%. So this means we'd actually rather convert the range of
         * 0.6 - 0.85 to a 0-1 value.  According to google, that would be (x - min) / (max - min),
         * or (icu - 0.6) / 0.25.
         */
        'signal': 'icuUtilization',
        'multnomah': (signals.multnomah.icu - 0.6) / 0.25,
        'washington': (signals.washington.icu - 0.6) / 0.25,
        'honolulu': (signals.honolulu.icu - 0.6) / 0.25,
        'clark': (signals.clark.icu - 0.6) / 0.25,
        'larimer': (signals.larimer.icu -0.6) / 0.25,
        'denver': (signals.denver.icu - 0.6) / 0.25,
        'elpaso': (signals.elpaso.icu - 0.6) / 0.25,
        // 'multnomah': signals.multnomah.icu / 0.85,
        // 'washington': signals.washington.icu / 0.85,
        // 'honolulu': signals.honolulu.icu / 0.85,
        // 'clark': signals.clark.icu / 0.85,
        // 'larimer': signals.larimer.icu / 0.85,
        // 'denver': signals.denver.icu / 0.85,
        // 'elpaso': signals.elpaso.icu / 0.85,
    }
]

// TODO expand scaling of ICU so that ~60% is zero
const MyResponsiveRadar = ({data, keys}: any) => {
    const region = keys[0];
    // console.log(data);

    // console.log(data.filter((struct) => struct.signal === 'icuUtilization'));
    const leftIcu = data.filter((struct: any) => struct.signal === 'icuUtilization')[0][region];
    const topRt = data.filter((struct: any) => struct.signal === 'rt')[0][region];
    const rightCases = data.filter((struct: any) => struct.signal === 'caseDensity')[0][region];
    const bottomTest = data.filter((struct: any) => struct.signal === 'testPositivity')[0][region];
    console.log(keys);
    console.log(`${leftIcu}, ${topRt}, ${rightCases}, ${bottomTest}`)

    return (
        <ResponsiveRadar
            data={data}
            keys={keys}
            indexBy="signal"
            maxValue={1.25}
            margin={{
                top: topRt > 1 ? (35 * topRt) + 15 : 15,
                right: rightCases > 1 ? (35 * rightCases) + 15 : 15,
                bottom: bottomTest > 1 ? (35 * bottomTest) + 15 : 15,
                left: leftIcu > 1 ? (35 * leftIcu) + 15 : 15}}
            curve="linearClosed"
            borderWidth={1}
            borderColor={{from: 'color'}}
            gridLevels={1.25}
            gridShape="circular"
            gridLabel={(_) => {
                return null;
            }}
            gridLabelOffset={36}
            enableDots={true}
            dotSize={10}
            // dotColor={{ theme: 'background' }}
            dotColor={(blah) => {
                if (blah.value > 1) {
                    return 'red';
                }
                return 'green';
            }}
            dotBorderWidth={2}
            dotBorderColor={(blah) => {
                if (blah.value > 1) {
                    return 'red';
                }
                return 'green';
            }}
            enableDotLabel={true}
            dotLabel={(blah) => {
                if (blah.index === "caseDensity") {
                    const realValue = blah.value * 10;
                    return 'Cases / 100k = ' + parseFloat(realValue.toString()).toFixed(1);
                } else if (blah.index === "testPositivity") {
                    const realPercent = blah.value * 0.05 * 100
                    return 'Test Positivity: ' + parseFloat(realPercent.toString()).toFixed(1) + "%"
                } else if (blah.index === "icuUtilization") {
                    // const realPercent = blah.value * 0.85 * 100
                    const realPercent = ((blah.value * 0.25) + 0.6) * 100
                    return 'ICU Usage: ' + parseFloat(realPercent.toString()).toFixed(0) + "%"
                }
                return 'Rt: ' + blah.value;
            }}
            dotLabelYOffset={-12}
            // colors={{scheme: 'nivo'}}
            colors={(keyContainer: { key: string, index: number }) => {
                return signals[keyContainer.key].color || 'gray';
            }}
            fillOpacity={0.15}
            blendMode="multiply"
            animate={true}
            // motionConfig="wobbly"
            isInteractive={false}
        />);
}

export const areas: Record<string, string> = {
    "multnomah": "Multnomah County",
    "washington": "Washington County",
    "honolulu": "Honolulu County",
    "clark": "Clark County",
    "larimer": "Larimer County",
    "denver": "Denver County",
    "elpaso": "El Paso County",
};

function App() {
    const [message, setMessage] = useState<string>('washington');
    const [data, setData] = useState(washingtonData);
    const vaccinesInitiated = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.vaccinationsInitiatedRatio, acc));
    const vaccinesCompleted = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.vaccinationsCompletedRatio, acc));

    const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        setMessage(e.target.value);
        setData(await import(`./data/${e.target.value}.json`));
    }

    return (
        <div className="App">
            <div style={{width: 700}}>
                <select defaultValue={message}
                        onChange={handleChange}
                >
                    <option value="multnomah">Multnomah County, OR (Portland)</option>
                    <option value="washington">Washington County, OR</option>
                    <option value="honolulu">Honolulu County, HI</option>
                    <option value="clark">Clark County, NV (Las Vegas)</option>
                    <option value="larimer">Larimer County, CO (Fort Collins)</option>
                    <option value="denver">Denver County, CO</option>
                    <option value="elpaso">El Paso County, CO (Colorado Springs)</option>
                </select>
                <p>
                    ({areas[message]} last updated {data.lastUpdatedDate}. Data is from the CovidActNow
                    API, which comes from the NYTimes data set.)
                </p>
                <p>
                    When trying to measure the impact of COVID in an area, the first step is determining
                    how many people have it. Diagnosing this officially requires a test.
                </p>
                <p>
                    Testing, however, is a challenge. At any given time, a collection of people is
                    either infected or uninfected, and they each choose whether or not to get tested.
                    (The following graph is a conceptual estimate; numbers not to scale.)
                </p>
                <div style={{width: 700, height: 300}}>
                    <TestPositivitySankey/>
                </div>
                <p>
                    "Test positivity" stats only calculate positives and negatives from lab tests,
                    so they miss undiagnosed cases, as well as home testing. Additionally, as infection
                    prevalence increases, if there aren't enough tests to meet the demand, test positivity
                    increases. Therefore, an increase in test positivity is an indication of an outbreak,
                    even if recorded case count still appears low. For this reason, the CDC says any
                    level above 5% is cause for concern.
                </p>
                <div style={{width: 700, height: 300}}>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        // border: '1px solid black',
                    }}>Test Positivity: {Math.round(signals[message].tests * 1000) / 10}%</span>
                    <TestPositivity data={data} message={message}/>
                </div>
                <p>
                    While testing may not capture every case, a rise in test positivity rate tends to
                    correlate with an increased level of cases. CDC tracks this through "daily new cases
                    per 100k population", and says there is increased virus prevalence when the metric
                    reaches 10 or above.
                </p>
                <div style={{width: 700, height: 300}}>
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    // border: '1px solid black',
                }}>Cases / 100k: {signals[message].cases }</span>
                    <CaseDensity data={data} message={message}/>
                </div>
                <p>
                    An effective reproductive number, or Rt, can be derived from the case count data and
                    other data. Rt is how many people an infected person infects on average. Any number
                    above 1 means the outbreak is growing over time. In other words, the cases will double
                    over some time period, more quickly as Rt increases. The shaded area is an estimate of
                    what regional Rt would be if not for the vaccines.
                </p>
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    // border: '1px solid black',
                }}>Rt: {signals[message].rt }</span>
                <Rt data={data} message={message}/>
                <p>
                    Rt responds to what level of mitigation is in place. Ultimately, without any other
                    mitigation, a disease runs its course by infecting everyone, multiple times if necessary,
                    until sufficient natural immunity is met. In the case of COVID-19, this approach would
                    cause far too much human suffering, including loss of life from other conditions due
                    to overcrowded hospitals. So we look for other forms of mitigation, including masks,
                    social distancing, lockdowns, and especially vaccinations - whatever is sufficient to
                    get an outbreak under control and get Rt under 1.
                </p>
                <p>
                    We can infer a target vaccination level given the current Rt, which changes as the
                    mitigation level changes. For instance, kids returning to school reduces social
                    distancing. As mitigation decreases, Rt increases, and therefore the target vaccination
                    level increases.
                </p>
                <p>
                    Target vaccination levels, indicated with patterned lines below, are determined through
                    herd immunity calculations. The calculation takes into account infection efficacy of vaccines,
                    combined with the reduced probability of vaccinated people passing on the virus to others.
                </p>
                <div style={{width: 700, height: 300}}>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        // border: '1px solid black',
                    }}>Vaccine #1: {Math.round(vaccinesInitiated * 1000) / 10}%, Vaccine #2: {Math.round(vaccinesCompleted * 1000) / 10}%</span>
                    <Vaccination data={data}/>
                </div>
                <p>
                    As a serious disease increases in prevalence, hospitalizations increase as well.
                    Ultimately, deaths increase too, both from the disease, and from hospitals unable to
                    give routine service due to overcrowding. So we also track ICU capacity. Anything above
                    85% utilization is considered critical:
                </p>
                <div style={{width: 700, height: 300}}>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        // border: '1px solid black',
                    }}>ICU Usage: {Math.round(signals[message].icu * 1000) / 10}%</span>
                    <Icu data={data} message={message}/>
                </div>
                <p>
                    I wanted to come up with a personal indicator that uses each of these signals to
                    help to determine what level of caution I should act with.
                </p>
                <p>
                    One thing that became clear is that the level of personal risk is different
                    than the level of community risk. Being vaccinated, my risk of serious
                    disease is lower than my risk of passing the virus to others, since viruses
                    have lower efficacy against infection than they do against hospitalization.
                </p>
                <p>
                    For that reason, when the disease is spreading or has high prevalence, I
                    don't want to contribute to spread. So I'm choosing to limit personal contact
                    - indoors, maskless gatherings with vaccinated people - when any of the
                    above indicators are above the limit.
                </p>
                <p>The signal is pretty conservative. Others may be comfortable being more social
                    if there is moderate ({'>'} 10) or even substantial ({'>'} 50) prevalence. But generally,
                    if Rt is greater than 1, it means the outbreak will get worse over time, so it's
                    a good signal to be in favor of greater temporary mitigation, until the outbreak
                    is under control again.
                </p>
                <p>
                    This signal can be drawn visually.
                </p>
                <p>
                    Washington County ({washingtonData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['washington']}/>
                </div>
                <p>
                    Multnomah County ({multnomahData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['multnomah']}/>
                </div>
                <p>
                    Here are some badges for other counties of family:
                </p>
                <p>
                    Clark County (Las Vegas, NV) ({clarkData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['clark']}/>
                </div>
                <p>
                    Honolulu County ({honoluluData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['honolulu']}/>
                </div>
                <p>
                    Larimer County (Fort Collins, CO) ({larimerData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['larimer']}/>
                </div>
                <p>
                    Denver County ({denverData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['denver']}/>
                </div>
                <p>
                    El Paso County (Colorado Springs, CO) ({elpasoData.lastUpdatedDate}):
                </p>
                <div style={{width: 700, height: 300}}>
                    <MyResponsiveRadar data={badgeData} keys={['elpaso']}/>
                </div>
            </div>
        </div>
    );
}

export default App;
